import {
  turboChargeDurationMs,
  type BonusRuntime,
} from "../application/bonus-runtime";
import type { GameplayLifecycle } from "../application/gameplay-lifecycle";

export interface BonusHudElements {
  readonly createStackLayer: () => HTMLElement;
  readonly turboButton: HTMLButtonElement;
  readonly turboStack: HTMLElement;
}

const formatSeconds = (milliseconds: number): string =>
  `${(milliseconds / 1_000).toFixed(1)}s`;

export class BonusHud {
  private readonly handleTurboClick = (event: MouseEvent): void => {
    event.stopPropagation();
    if (event.detail === 0 && this.canActivateTurbo()) {
      this.runtime.activateTurbo();
    }
  };
  private readonly handleTurboContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };
  private readonly handleTurboPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (event.button === 0 && this.canActivateTurbo()) {
      this.runtime.activateTurbo();
    }
  };
  private readonly handleTurboPointerEnd = (event: PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };
  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (
      event.code !== "Space" ||
      event.repeat ||
      event.defaultPrevented ||
      this.hasFocusedControl()
    ) {
      return;
    }
    const lifecycle = this.lifecycle.getSnapshot();
    const bonuses = this.runtime.getSnapshot();
    if (
      lifecycle.scene !== "gameplay" ||
      lifecycle.paused ||
      bonuses.turboActiveRemainingMs > 0 ||
      bonuses.turboBankedMs <= 0
    ) {
      return;
    }
    if (this.runtime.activateTurbo()) {
      event.preventDefault();
    }
  };
  private readonly unsubscribers: readonly (() => void)[];

  public constructor(
    private readonly documentScope: Document,
    private readonly lifecycle: GameplayLifecycle,
    private readonly runtime: BonusRuntime,
    private readonly elements: BonusHudElements,
  ) {
    this.elements.turboButton.addEventListener(
      "pointerdown",
      this.handleTurboPointerDown,
    );
    this.elements.turboButton.addEventListener(
      "pointerup",
      this.handleTurboPointerEnd,
    );
    this.elements.turboButton.addEventListener(
      "pointercancel",
      this.handleTurboPointerEnd,
    );
    this.elements.turboButton.addEventListener("click", this.handleTurboClick);
    this.elements.turboButton.addEventListener(
      "contextmenu",
      this.handleTurboContextMenu,
    );
    this.documentScope.addEventListener("keydown", this.handleKeyDown);
    this.unsubscribers = [
      lifecycle.subscribe(() => {
        this.render();
      }),
      runtime.subscribe(() => {
        this.render();
      }),
    ];
    this.render();
  }

  public destroy(): void {
    this.elements.turboButton.removeEventListener(
      "pointerdown",
      this.handleTurboPointerDown,
    );
    this.elements.turboButton.removeEventListener(
      "pointerup",
      this.handleTurboPointerEnd,
    );
    this.elements.turboButton.removeEventListener(
      "pointercancel",
      this.handleTurboPointerEnd,
    );
    this.elements.turboButton.removeEventListener(
      "click",
      this.handleTurboClick,
    );
    this.elements.turboButton.removeEventListener(
      "contextmenu",
      this.handleTurboContextMenu,
    );
    this.documentScope.removeEventListener("keydown", this.handleKeyDown);
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
  }

  private hasFocusedControl(): boolean {
    const activeElement = this.documentScope.activeElement;
    return (
      activeElement !== null &&
      activeElement.matches(
        'button, input, select, textarea, a[href], [contenteditable="true"]',
      ) &&
      activeElement.closest("[hidden]") === null
    );
  }

  private canActivateTurbo(): boolean {
    const lifecycle = this.lifecycle.getSnapshot();
    const bonuses = this.runtime.getSnapshot();
    return (
      lifecycle.scene === "gameplay" &&
      !lifecycle.paused &&
      bonuses.turboActiveRemainingMs === 0 &&
      bonuses.turboBankedMs > 0
    );
  }

  private render(): void {
    const snapshot = this.runtime.getSnapshot();
    const lifecycle = this.lifecycle.getSnapshot();
    const active = snapshot.turboActiveRemainingMs > 0;
    const queuedCharges = snapshot.turboBankedMs / turboChargeDurationMs;
    const totalCharges = queuedCharges + (active ? 1 : 0);
    const progress = active
      ? snapshot.turboActiveRemainingMs / turboChargeDurationMs
      : 1;
    const gameplayActive = lifecycle.scene === "gameplay" && !lifecycle.paused;
    this.elements.turboButton.hidden = !gameplayActive || totalCharges <= 0;
    this.elements.turboButton.disabled = !gameplayActive;
    this.elements.turboButton.setAttribute(
      "aria-disabled",
      String(active || !gameplayActive),
    );
    this.elements.turboButton.dataset.turboState = active ? "active" : "ready";
    this.elements.turboButton.style.setProperty(
      "--turbo-progress",
      `${String(progress * 360)}deg`,
    );
    const layers = Array.from(
      { length: Math.max(0, totalCharges - 1) },
      (_, index) => {
        const layer = this.elements.createStackLayer();
        layer.className = "snakely-turbo-charge-layer";
        layer.style.setProperty("--turbo-progress", "360deg");
        layer.style.setProperty("--turbo-stack-index", String(index + 1));
        return layer;
      },
    );
    this.elements.turboStack.replaceChildren(...layers);
    const queueLabel = `${String(queuedCharges)} ${queuedCharges === 1 ? "charge" : "charges"} queued`;
    this.elements.turboButton.setAttribute(
      "aria-label",
      active
        ? `Turbo active, ${formatSeconds(snapshot.turboActiveRemainingMs)} remaining, ${queueLabel}`
        : `Activate Turbo, ${queueLabel}`,
    );
  }
}

export const installBonusHud = (
  documentScope: Document,
  lifecycle: GameplayLifecycle,
  runtime: BonusRuntime,
): BonusHud => {
  const turboButton = documentScope.getElementById("turbo-button");
  const turboStack = documentScope.getElementById("turbo-stack");
  if (turboButton === null || turboStack === null) {
    throw new Error("Missing Turbo control");
  }
  return new BonusHud(documentScope, lifecycle, runtime, {
    createStackLayer: () => documentScope.createElement("span"),
    turboButton: turboButton as HTMLButtonElement,
    turboStack,
  });
};
